import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { SideNavComponent } from './side-nav/side-nav.component';
import { TranslateService } from '@ngx-translate/core';
import { availableLanguages, defaultLanguage, } from './services/indexed-db/object-stores/settings/settings';
import {
  currentLanguageSettingsKey,
  SettingsService,
} from './services/indexed-db/object-stores/settings/settings.service';
import { HeaderService } from './header-section/header-service/header.service';
import { LoginComponent } from './login/login.component';
import { LoginService } from './login/login.service';
import { take } from 'rxjs';

import {
  RequestLoadingIndicatorComponent
} from './reusable-components/request-loading-indicator/request-loading-indicator.component';
import { NetworkStatusService } from './services/network-status/network-status.service';
import { SessionTimeoutService } from './services/session-timeout/session-timeout.service';
import { RouteSettingsService } from './route-settings/route-settings.service';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SideNavComponent,
    LoginComponent,
    RequestLoadingIndicatorComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly msalService = inject(MsalService);

  constructor(
    private readonly translate: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly headerService: HeaderService,
    protected readonly loginService: LoginService,
    private readonly router: Router,
    private readonly networkStatusService: NetworkStatusService,
    private readonly sessionTimeoutService: SessionTimeoutService,
    private readonly routeSettingsService: RouteSettingsService,
  ) {
    this.translate.addLangs(availableLanguages);
    this.translate.setFallbackLang(defaultLanguage);
    // instantiated here and used to notify the user about network status changes
    this.networkStatusService.initNetworkMonitoring();
    // Initialize session timeout monitoring
    this.sessionTimeoutService.initializeSessionTimeout();

    //We do not initialize in the RouteSettingsService constructor to force the app.component the initialization and not on the fly
    this.routeSettingsService.initialize();
  }

  title = 'Login';

  // Detect various user interactions to reset the session timeout timer
  // This includes touch, mouse, keyboard, scroll, wheel, and resize events
  // Touch events for mobile
  @HostListener('touchstart')
  @HostListener('touchend')
  @HostListener('touchmove')
  // Mouse events for desktop (keeping these for hybrid devices)
  @HostListener('mousedown')
  @HostListener('mousemove')
  // Keyboard events
  @HostListener('keydown')
  @HostListener('keyup')
  // Other interaction events
  @HostListener('scroll')
  @HostListener('wheel')
  @HostListener('resize')
  onUserActivity() {
    this.sessionTimeoutService.onUserActivity();
  }

  ngOnInit() {
    // Process MSAL redirect responses (runs silently on every page load)
    this.msalService.handleRedirectObservable().subscribe();

    this.settingsService.getSetting(currentLanguageSettingsKey).then(
      (languageSetting) => {
        if (languageSetting === undefined) {
          void this.settingsService.addSetting({
            key: currentLanguageSettingsKey,
            value: defaultLanguage,
          });
        }

        this.translate
          .use(languageSetting?.value ?? defaultLanguage)
          .pipe(take(1))
          .subscribe(() => {
            this.headerService.updateHeader();
          });
      },
      (error) => console.error('Error getting language from settings', error),
    );
  }

  ngOnDestroy(): void {
    this.sessionTimeoutService.destroy();
  }

  isAuthCallback(): boolean {
    return this.router.url.startsWith('/auth');
  }

  isMediaInputRoute(): boolean {
    return this.router.url.includes('/media-input');
  }

  isLogoutRoute(): boolean {
    return this.router.url.startsWith('/logout');
  }
}
